import { DataDai } from '../domain/dai/types';
import { ColumnForDataTable, Item, NestedColumn, Row } from '../domain/schemas';

import { insert } from './insert';

/** 標準表示で常に先頭固定表示する列のコード（機種名・台番号） */
export const STANDARD_STICKY_COLUMN_CODES = ['kiTushoMei', 'daiBan'];

/**
 * 二段組ヘッダーの列を平らな配列にして比較。
 */
export const flattenColumns = (columns: NestedColumn[]): ColumnForDataTable[] =>
  columns.flatMap((column) =>
    column.columns && column.columns.length > 0 ? column.columns : [column]
  );

/**
 * 指定コードの列の値が、行データ row.data の何番目（セル位置）に入っているかを返す（無ければ -1）。
 *
 * 用途: 台別データ一覧で「機種名（kiTushoMei）列」の値を各行から取り出す箇所で使う。
 *   - 機種名フィルタ（dataDaiSelector）: 各行の機種名セルの値で絞り込む
 *   - 機種セレクトの選択肢（dataDaiSelectOptionsSelector）: 各行の機種名を選択肢名にする
 *   - 機種名列の横幅計算（UnitDataListTable の kiNameFieldWidth）
 *   いずれも「機種名が row.data の何番目か」が必要だが、その位置は表示区分で変わるため本関数で求める。
 *
 * ■なぜ位置決め打ち（row.data.at(0) 等）ではダメか
 *   台別データ一覧は、標準表示と出玉ランキングでレスポンスの列の並びが違う:
 *     標準         : [機種名, 台番号, ...]        → 機種名は 0 番目
 *     出玉ランキング : [日付, 機種名, 台番号, ...]  → 機種名は 1 番目
 *   そのため「機種名は先頭」と決め打ちすると、ランキングでは機種名のつもりで
 *   日付を読んでしまう。列コードで探すことで、並びが変わっても正しい列を指せる。
 */
export const findCellIndexByColumnCode = (
  columns: NestedColumn[],
  code: string
): number =>
  flattenColumns(columns).findIndex((column) => column.code === code);

/**
 * 固定表示する列とそれ以外の列に分割する。
 *
 * Why:
 * - テーブルヘッダーとボディが別々に「固定列を先頭にする」並び替えを行うと、
 *   APIレスポンスの列順が固定列始まりでない場合（例：出玉ランキング）に
 *   ヘッダーとセルの対応がずれてしまう。ヘッダー・ボディ双方から本関数を使うことで
 *   同一ロジックで並びを揃える。
 * - 固定列の並び順は `stickyColumnCodes` で指定した順序に固定する
 *   （APIレスポンスの列順に依存すると、固定列同士の前後関係が表示ごとに変わりうるため）。
 *
 * @param items 分割対象の一覧（列情報 or セルと列のペア等）
 * @param getCode 各要素から列コードを取得する関数
 * @param stickyColumnCodes 固定表示する列コード（この順序で先頭に並ぶ）。省略時は標準の機種名・台番号
 */
export const partitionStickyColumns = <T>(
  items: T[],
  getCode: (item: T) => string | null | undefined,
  stickyColumnCodes: string[] = STANDARD_STICKY_COLUMN_CODES
): { sticky: T[]; normal: T[] } => {
  const sticky = stickyColumnCodes
    .map((code) => items.find((item) => getCode(item) === code))
    .filter((item): item is T => item !== undefined);

  return {
    sticky,
    normal: items.filter(
      (item) => !stickyColumnCodes.includes(getCode(item) ?? '')
    ),
  };
};

/**
 * 固定列を常に先頭に置いた列コード順を組み立てる。
 *
 * Why:
 * - 「固定列を先頭にする」処理をAPIレスポンスの列位置（先頭2つ等）で行うと、
 *   レスポンスの列順が固定列始まりでない場合（例：出玉ランキングは日付が先頭）に
 *   固定列とは別の列を誤って先頭固定してしまい、並び替えが再取得で巻き戻る。
 *   位置ではなく列コードで固定列を判定することでモードに依らず正しく先頭固定する。
 *
 * @param fieldCodes 現在存在する列コード（APIレスポンスの列順）
 * @param columnsOrder 現在の並び順（この順で非固定列を並べる）
 * @param stickyColumnCodes 先頭固定にする列コード（この順で先頭に並ぶ）。省略時は標準の機種名・台番号
 * @returns 固定列を先頭にした列コード順
 */
export const buildStickyFirstColumnsOrder = (
  fieldCodes: string[],
  columnsOrder: string[],
  stickyColumnCodes: string[] = STANDARD_STICKY_COLUMN_CODES
): string[] => {
  // 実際に存在する固定列のみを、指定順で先頭に並べる
  const stickyFieldCodes = stickyColumnCodes.filter((code) =>
    fieldCodes.includes(code)
  );
  const restFieldCodes = fieldCodes.filter(
    (code) => !stickyFieldCodes.includes(code)
  );

  // columnsOrder に存在しない列コード（fields追加・モード切替直後などで
  // 並び順が不完全なケース）は indexOf が -1 となる。そのまま差分ソートすると
  // -1 が正規のindexより小さく先頭へ割り込むため、見つからない列は末尾扱いにする。
  const orderIndex = (code: string): number => {
    const index = columnsOrder.indexOf(code);
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  };

  return [
    ...stickyFieldCodes,
    ...restFieldCodes.sort((a, b) => orderIndex(a) - orderIndex(b)),
  ];
};

/**
 * 並び替え後の並び替え情報を生成する
 * @param orderedColumns 並び替え対象のテーブルのカラム一覧
 * @param fromId 並び替え対象のカラムのID
 * @param toId 移動させる位置にあるカラムのID
 * @returns 並び替え後の並び替え情報
 */
export const recalcColumnOrder = (
  orderedColumns: {
    code: string | null;
  }[],
  fromId: string | undefined,
  toId: string | undefined
) => {
  if (fromId == null || toId == null || fromId === toId) {
    return [];
  }
  const fromIndex = orderedColumns.findIndex(
    (column) => column.code === fromId
  );
  const toIndex = orderedColumns.findIndex((column) => column.code === toId);

  // columns を desiredSort の順に並び替える
  const resultColumns = insert(orderedColumns, fromIndex, toIndex);

  return resultColumns
    .map((colmun) => colmun.code)
    .filter((code) => code != null) as string[];
};

/**
 * 並び替え情報を元にテーブルデータを並び替える
 * @param desiredOrderCode 並び替え情報
 * @param columns テーブルのカラム
 * @param rows テーブルのデータ部分
 * @returns 並び替えたテーブルデータ
 */
export const getOrderedTableData = <
  T extends Row[] | DataDai['data']['rows'],
  K extends {
    code: string | null;
    columns?: { code: string | null }[];
  }[]
>(
  desiredOrderCode: string[],
  columns: K,
  rows: T
): { rows: T; columns: K } => {
  // TODO:
  // お気に入り適用時、fetch完了前のcolumnsには存在していないことがあり
  // そのタイミングでエラーをスローしています。再render時にこの問題は解消されますが
  // 1. 使用側でこの関数を呼び出すタイミングを考慮する
  // 2. undefinedが戻されることを考慮する
  // などの対応をしたほうが良いかもしれません。
  try {
    const orderedColumns = desiredOrderCode.map((code) => {
      const index = columns.findIndex((column) => column.code === code);

      if (index === -1) {
        throw new Error('並び替え対象のカラムIDが存在しません');
      }

      return columns[index];
    }) as K;

    const rowDesiredOrderCodes = desiredOrderCode.reduce<(string | null)[]>(
      (prev, current) => {
        const column = columns.find((column) => column.code === current);

        if (!column) {
          throw new Error('並び替え対象のカラムIDが存在しません');
        }

        // 一つの列で複数のアイテムを所持する場合、
        // 子カラムのnameに差し替えてflatな構造の配列にする
        if (column?.columns) {
          const columnNames = column.columns.map((val) => val.code);
          return [...prev, ...columnNames];
        }
        return [...prev, column.code];
      },
      []
    );

    const rowDesiredOrderIndexes = rowDesiredOrderCodes.map<number>((code) => {
      let count = 0;
      for (const column of columns) {
        if (column.code === code) {
          return count;
        }
        if (column.columns) {
          for (const childColumn of column.columns) {
            if (childColumn.code === code) {
              return count;
            }
            count++;
          }
          continue;
        }
        count++;
      }

      throw new Error('並び替え対象のカラムIDが存在しません');
    });

    const emptyOrderedCell: Item = {
      value: '-',
      color: '#000000',
      backgroundColor: null,
    };

    const orderedRows = rows.map((row) => {
      return {
        ...row,
        data: rowDesiredOrderIndexes.map(
          (i) => row.data.at(i) ?? emptyOrderedCell
        ),
      };
    }) as T;

    return {
      columns: orderedColumns,
      rows: orderedRows,
    };
  } catch {
    return { columns, rows };
  }
};
